import { useCallback, useEffect, useState } from 'react';
import { SHOW_REPORTS_LIST_EVENT } from 'app/pendingReports';

/**
 * Manages which single header dropdown panel is open at a time (mirrors the
 * Backbone header: `click_nav_item` closes all `.nav-list` then toggles the
 * target; `openCloseList` toggles the reports list; a document-click closes
 * every open list — except clicks on an <A> element, per header.js line 120).
 *
 * Only one of 'reports' | 'notifications' | 'release_notes' is open at once.
 */
export type HeaderPanel = 'reports' | 'notifications' | 'release_notes' | null;

export const useHeaderPanel = () => {
  const [openPanel, setOpenPanel] = useState<HeaderPanel>(null);

  const toggle = useCallback((panel: Exclude<HeaderPanel, null>) => {
    setOpenPanel((cur) => (cur === panel ? null : panel));
  }, []);

  const closeAll = useCallback(() => setOpenPanel(null), []);

  // 5b: the reports board auto-opens the Recent-Reports panel 5s after
  // Generate — `$('.generated-reports-list').removeClass('hide')`
  // (report controller/action.js:130-133), bridged as a window event.
  useEffect(() => {
    const onShow = () => setOpenPanel('reports');
    window.addEventListener(SHOW_REPORTS_LIST_EVENT, onShow);
    return () => window.removeEventListener(SHOW_REPORTS_LIST_EVENT, onShow);
  }, []);

  // Document click closes any open list (header.js $(document).click). Clicks on
  // an <A> are ignored there (so nav links still work); the panel toggles call
  // stopPropagation, so a click inside an open panel does not reach here.
  useEffect(() => {
    if (openPanel == null) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.tagName === 'A') return;
      setOpenPanel(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openPanel]);

  return { openPanel, toggle, closeAll };
};
